import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import Finance from '~/@lib/utils/finance.utils'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import {
  calculateAverageCompoundedYield,
  calculateAverageCompoundedYields,
} from '~/@pages_investing/_utils/compoundedYield.utils'
import { calcExtraYieldPerAnnum } from '~/@pages_investing/_utils/revenueFee.utils'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'
import type { ChartDataItem } from './types'

export const useProjectInvestmentProjection = (
  project: ProjectSchema,
  alwaysApplyRevenueFee: boolean = false
) => {
  const { calculateFeeNettoAmount, calculateFeeNettoAmountUnrounded } =
    useRevenueFee(alwaysApplyRevenueFee)
  const {
    calculateFeeNettoAmount: calculateFeeNettoAmountWithoutRevenueFee,
    calculateFeeNettoAmountUnrounded:
      calculateFeeNettoAmountUnroundedWithoutRevenueFee,
  } = useRevenueFee(false)
  const isProjectYieldCompoundingAnnualy =
    project.yieldCompounding === IYieldCompounding.Annually

  function getCompoundedAverages(investmentAmount: number) {
    const totalYieldPerAnnumBrutto = getTotalYieldPerAnnum(investmentAmount)
    const { compoundedYieldPerAnnumAverageTotal } =
      calculateAverageCompoundedYields(
        totalYieldPerAnnumBrutto,
        project.investmentPeriod
      )

    const compoundedUnitMonthlyFactor = 1 + totalYieldPerAnnumBrutto / 100 / 12
    const compoundedYieldPerAnnumAverageTotalRevenueBrutto =
      Math.pow(compoundedUnitMonthlyFactor, project.investmentPeriod) *
        investmentAmount -
      investmentAmount

    return {
      compoundedYieldPerAnnumAverageTotal: calculateFeeNettoAmount(
        compoundedYieldPerAnnumAverageTotal
      ),
      compoundedYieldPerAnnumAverageTotalRevenue: calculateFeeNettoAmount(
        compoundedYieldPerAnnumAverageTotalRevenueBrutto
      ),
      compoundedYieldPerAnnumAverageTotalReturnAmount:
        Finance.round(
          calculateFeeNettoAmountUnrounded(
            compoundedYieldPerAnnumAverageTotalRevenueBrutto
          )
        ) + investmentAmount,

      compoundedYieldPerAnnumAverageTotalBrutto:
        calculateFeeNettoAmountWithoutRevenueFee(
          compoundedYieldPerAnnumAverageTotal
        ),
      compoundedYieldPerAnnumAverageTotalRevenueBrutto:
        calculateFeeNettoAmountWithoutRevenueFee(
          compoundedYieldPerAnnumAverageTotalRevenueBrutto
        ),
      compoundedYieldPerAnnumAverageTotalReturnAmountBrutto:
        Finance.round(
          calculateFeeNettoAmountUnroundedWithoutRevenueFee(
            compoundedYieldPerAnnumAverageTotalRevenueBrutto
          )
        ) + investmentAmount,
    }
  }

  function getProjectInvestmentChartData(investmentAmount: number) {
    const months = project.investmentPeriod
    const totalYieldPerAnnumBrutto = getTotalYieldPerAnnum(investmentAmount)
    const annualRateNetto =
      calculateFeeNettoAmountUnrounded(totalYieldPerAnnumBrutto) / 100
    const compoundedUnitMonthlyFactor = 1 + totalYieldPerAnnumBrutto / 100 / 12
    const linearMonthlyYieldNetto = annualRateNetto / 12

    const data: ChartDataItem[] = []
    for (let month = 0; month <= months; month++) {
      // compounded values calculation
      const currentMonthCompoundedYieldBrutto =
        Math.pow(compoundedUnitMonthlyFactor, month) - 1
      const currentMonthCompoundedYieldNetto =
        calculateFeeNettoAmountUnrounded(
          currentMonthCompoundedYieldBrutto * 100
        ) / 100
      const currentMonthCompoundedFactorNetto =
        1 + currentMonthCompoundedYieldNetto
      const compoundedReturnValueNetto =
        investmentAmount * currentMonthCompoundedFactorNetto
      const compoundedRevenueValueNetto =
        compoundedReturnValueNetto - investmentAmount

      // linear values calculation
      const currentMonthLinearYieldNetto = linearMonthlyYieldNetto * month
      const currentMonthLinearReturnValueNetto =
        investmentAmount * (1 + currentMonthLinearYieldNetto)
      const currentMonthLinearRevenueValueNetto =
        currentMonthLinearReturnValueNetto - investmentAmount

      // current average compounded yield p.a.
      const currentCompoundedYieldPerAnnumAverage =
        calculateAverageCompoundedYield(totalYieldPerAnnumBrutto, month)

      data.push({
        months: month,
        'Rastúce mesačné úročenie': Finance.round(compoundedRevenueValueNetto),
        'Mesačne vyplácané úroky %': Finance.round(
          currentMonthLinearRevenueValueNetto
        ),
        compoundRoi: Finance.round(currentMonthCompoundedYieldNetto * 100),
        simpleRoi: Finance.round(currentMonthLinearYieldNetto * 100),
        currentCompoundedYieldPerAnnumAverage: Finance.round(
          calculateFeeNettoAmountUnrounded(
            currentCompoundedYieldPerAnnumAverage
          )
        ),
      })
    }
    return data
  }

  function getTotalYieldPerAnnum(investmentAmount: number) {
    const fixedYieldPerAnnum = Finance.round(project.scheme.fixedYield)

    const extraYieldPerAnnum = calcExtraYieldPerAnnum(
      project.extraYieldSettings,
      investmentAmount
    )

    return Finance.round(fixedYieldPerAnnum + extraYieldPerAnnum)
  }

  function getTotalYieldPerAnnumNetto(investmentAmount: number) {
    const totalYieldPerAnnum = getTotalYieldPerAnnum(investmentAmount)
    return calculateFeeNettoAmount(totalYieldPerAnnum)
  }

  return {
    isProjectYieldCompoundingAnnualy,
    getProjectInvestmentChartData,
    getTotalYieldPerAnnumNetto,
    getCompoundedAverages,
  }
}
